import { zodResolver } from '@hookform/resolvers/zod';
import AddressFormatter from '@shopify/address';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import PostalCodeValidator from 'postal-codes-js';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  getCityErrorMessage,
  getPostalCodeErrorMessage,
  getZoneErrorMessage
} from './billing-address-validation-errors';

const billingAddressSchema = (intl, billingCountries) => {
  return z
    .object({
      country: z.string().min(2, {
        message: intl.formatMessage({
          id: 'billing_address_form.country_validation',
          defaultMessage: 'Please select a country'
        })
      }),
      address1: z.string().min(1, {
        message: intl.formatMessage({
          id: 'billing_address_form.street_address_validation',
          defaultMessage: 'Please enter a street address'
        })
      }),
      city: z.string(),
      state: z.string(),
      zip: z.string()
    })
    .refine(
      ({ city }) => {
        return city?.length > 0;
      },
      ({ country }) => {
        const selectedCountryObj = billingCountries.find(
          (countryObj) => countryObj.code === (country || 'US')
        );
        return {
          path: ['city'],
          message: getCityErrorMessage(intl, selectedCountryObj?.labels?.city)
        };
      }
    )
    .refine(
      ({ state }) => {
        return state?.length > 0;
      },
      ({ country }) => {
        const selectedCountryObj = billingCountries.find(
          (countryObj) => countryObj.code === (country || 'US')
        );
        return {
          path: ['state'],
          message: getZoneErrorMessage(intl, selectedCountryObj?.labels?.zone)
        };
      }
    )
    .refine(
      ({ zip, country }) => {
        const validationRes = PostalCodeValidator.validate(country, zip);
        return validationRes === true;
      },
      ({ country }) => {
        const selectedCountryObj = billingCountries.find(
          (countryObj) => countryObj.code === (country || 'US')
        );
        return {
          path: ['zip'],
          message: getPostalCodeErrorMessage(
            intl,
            selectedCountryObj?.labels?.postal_code
          )
        };
      }
    );
};

export const useBillingAddressForm = ({
  intl,
  defaultValues,
  onChange,
  debounceDelay = 1000
}) => {
  const [billingCountries, setBillingCountries] = React.useState([]);

  const billingAddressForm = useForm({
    resolver: zodResolver(billingAddressSchema(intl, billingCountries)),
    defaultValues: {
      country: (intl?.locale || 'en-US').split('-')[1],
      address1: '',
      city: '',
      state: '',
      zip: '',
      ...(defaultValues || {})
    }
  });

  const prevFormValues = React.useRef(
    cloneDeep(billingAddressForm.getValues())
  );
  const getChangedFields = () => {
    const newValues = billingAddressForm.getValues();
    const changedFields = Object.keys(newValues).filter(
      (field) => !isEqual(newValues[field], prevFormValues.current[field])
    );

    return changedFields;
  };

  const debouncedOnChange = debounce((newValues) => {
    onChange?.(newValues, getChangedFields(newValues));
    prevFormValues.current = cloneDeep(newValues);
  }, debounceDelay);

  React.useEffect(() => {
    const formSubscription = billingAddressForm.watch(debouncedOnChange);
    return () => {
      formSubscription?.unsubscribe();
    };
  }, []);

  const fetchCountries = async () => {
    const addressFormatter = new AddressFormatter(intl.locale);
    const countries = await addressFormatter.getCountries();
    setBillingCountries(countries);
  };

  React.useEffect(() => {
    fetchCountries();
  }, []);

  return { billingAddressForm, billingCountries };
};
